import axios from "axios";
import { FULFILLED, PENDING, REJECTED } from "./action-type.util";

export interface IBrokerImage {
  brokerID: number;
  imageUrl?: string;
}

export const ACTION_TYPES = {
  FETCH_BROKER_LOGO: "brokerImages/FETCH_BROKER_LOGO",
  UPLOAD_BROKER_LOGO: "brokerImages/UPLOAD_BROKER_LOGO",
  DELETE_BROKER_LOGO: "brokerImages/DELETE_BROKER_LOGO",
};

export interface BrokerImageState {
  loading: boolean;
  images: Array<IBrokerImage>;
  uploadError: string | null;
}

const initialState: BrokerImageState = {
  loading: false,
  images: [],
  uploadError: null,
};

const upsert = (images: IBrokerImage[], brokerID: number, patch: Partial<IBrokerImage>) => {
  const idx = images.findIndex(x => x.brokerID === brokerID);
  if (idx >= 0) images[idx] = { ...images[idx], ...patch, brokerID };
  else images.push({ brokerID, ...(patch as any) });
};

export default (state: BrokerImageState = initialState, action): BrokerImageState => {
  switch (action.type) {
    case PENDING(ACTION_TYPES.FETCH_BROKER_LOGO):
    case PENDING(ACTION_TYPES.DELETE_BROKER_LOGO):
      return { ...state, loading: true };

    case PENDING(ACTION_TYPES.UPLOAD_BROKER_LOGO):
      return { ...state, loading: true, uploadError: null };

    case FULFILLED(ACTION_TYPES.FETCH_BROKER_LOGO): {
      const { status, data } = action.payload as { status: number; data: Blob };
      const brokerID = action.meta.brokerID as number;

      if (status === 404) {
        return {
          ...state,
          loading: false,
          images: state.images.filter(x => x.brokerID !== brokerID),
        };
      }

      const objectUrl = URL.createObjectURL(data);
      const images = [...state.images];
      upsert(images, brokerID, { imageUrl: objectUrl });
      return { ...state, loading: false, images };
    }

    case FULFILLED(ACTION_TYPES.UPLOAD_BROKER_LOGO): {
      const { status, data } = action.payload as { status: number; data: Blob };
      const brokerID = action.meta.brokerID as number;

      if (status === 404) {
        return {
          ...state,
          loading: false,
          images: state.images.filter(x => x.brokerID !== brokerID),
        };
      }

      const objectUrl = URL.createObjectURL(data);
      const images = [...state.images];
      upsert(images, brokerID, { imageUrl: objectUrl });
      return { ...state, loading: false, images };
    }

    case FULFILLED(ACTION_TYPES.DELETE_BROKER_LOGO): {
      const brokerID = action.meta?.brokerID as number;
      return {
        ...state,
        loading: false,
        images: state.images.filter(x => x.brokerID !== brokerID),
      };
    }

    case REJECTED(ACTION_TYPES.FETCH_BROKER_LOGO):
    case REJECTED(ACTION_TYPES.DELETE_BROKER_LOGO):
      return { ...state, loading: false };

    case REJECTED(ACTION_TYPES.UPLOAD_BROKER_LOGO):
      return { ...state, loading: false, uploadError: "Error Uploading Broker Logo" };

    default:
      return state;
  }
};

export const fetchBrokerLogo = (brokerID: number) => ({
  type: ACTION_TYPES.FETCH_BROKER_LOGO,
  meta: { brokerID },
  payload: axios.get(`brokers/${brokerID}/brokerLogo`, {
    responseType: "blob",
    validateStatus: s => s === 200 || s === 404,
  }),
});

export const uploadBrokerLogo = (brokerID: number, formData: FormData, onProgress?: (percent: number) => void) => ({
  type: ACTION_TYPES.UPLOAD_BROKER_LOGO,
  meta: { brokerID },
  payload: axios
    .post(`brokers/${brokerID}/uploadBrokerLogo`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      validateStatus: s => s >= 200 && s < 300,
      onUploadProgress: e => {
        if (onProgress && e.total) {
          const percent = Math.round((e.loaded * 100) / e.total);
          onProgress(percent);
        }
      },
    })
    .then(() =>
      axios.get(`brokers/${brokerID}/brokerLogo`, {
        responseType: "blob",
        validateStatus: s => s === 200 || s === 404,
      }),
    ),
});

export const deleteBrokerLogo = (brokerID: number) => ({
  type: ACTION_TYPES.DELETE_BROKER_LOGO,
  meta: { brokerID },
  payload: axios.delete(`brokers/${brokerID}/deleteBrokerLogo`),
});
